import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, RotateCcw } from 'lucide-react';
import { Button, Card } from './ui';

/**
 * The floor under a page that throws while drawing.
 *
 * Until 2026-09-13 there was none. The bulk page fell into a state loop after
 * a one-ONU resync (see `nextRunningId`), React stopped it with "Maximum
 * update depth exceeded", and with nothing above the page to catch the throw
 * React unmounted the WHOLE tree — sidebar, header, the run banner — and left
 * a white window. A crash in one page's render should cost that page, not
 * the shell, and the operator should be able to read what happened and try
 * again without a full reload.
 *
 * A class because React still gives error boundaries no hook: only
 * `getDerivedStateFromError` and `componentDidCatch` exist, and both are
 * class lifecycles.
 *
 * Two dresses for two places. `card` sits inside the shell's `<main>` and
 * uses the app's own primitives, so it looks like a page saying something
 * rather than a browser saying something. `plain` is the last resort at the
 * very root, ABOVE the router, the query client and the session, and it
 * deliberately imports nothing that could itself be what threw: inline
 * styles on the design tokens, plain buttons.
 *
 * `resetKey` clears a caught error when it changes — the shell passes the
 * pathname, so leaving a broken page for another one draws the other one.
 * A `key=` on the boundary would do the same and was the first draft, but a
 * keyed boundary remounts its CHILDREN on every pathname change, and
 * `/onus/25` → `/onus/26` is today the same page keeping its tab and its
 * scroll; that is not a cost an error floor should charge every navigation.
 * The reset happens only while an error is being shown.
 */
interface Props {
  children: ReactNode;
  variant?: 'card' | 'plain';
  resetKey?: string;
}

interface State {
  error: unknown;
}

/** What the notice quotes: the message when there is one, never `[object Object]`. */
function describe(error: unknown): string {
  if (error instanceof Error) return error.message || error.name || 'Error';
  if (typeof error === 'string' && error.length > 0) return error;
  return 'An error with no message';
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error: error ?? new Error('Unknown error') };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    /*
      The console is where this goes, on purpose: the operator's browser is
      the only place the component stack exists, and a support call that
      starts with "open the console" is answerable. Nothing is posted to the
      API from here — an error handler that makes a request is an error
      handler that can loop.
    */
    console.error('[ErrorBoundary] a page threw while rendering', error, info.componentStack);
  }

  override componentDidUpdate(previous: Props): void {
    if (this.state.error !== null && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  private readonly retry = (): void => {
    this.setState({ error: null });
  };

  private readonly reload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    const message = describe(error);
    if (this.props.variant === 'plain')
      return <PlainNotice message={message} retry={this.retry} reload={this.reload} />;

    return (
      <Card title="This page hit an error" className="mx-auto max-w-2xl border-danger/30">
        <div className="grid gap-4 p-5">
          <div className="flex items-start gap-3 text-sm text-muted">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-danger" />
            <p>
              Something on this page failed while it was being drawn. Nothing was written to any
              chassis or CPE by this — the page stopped, not the platform. The menu still works; the
              details are in the browser console.
            </p>
          </div>
          <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-lg border border-line bg-surface-2 px-3 py-2 font-mono text-xs text-danger">
            {message}
          </pre>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="primary" icon={<RotateCcw className="size-4" />} onClick={this.retry}>
              Try again
            </Button>
            <Button icon={<RefreshCw className="size-4" />} onClick={this.reload}>
              Reload
            </Button>
          </div>
        </div>
      </Card>
    );
  }
}

/**
 * The root notice: no `Card`, no `Button`, no Tailwind class that the build
 * might not have emitted — only the tokens `index.css` defines on `:root`,
 * which are there whatever else failed to load.
 */
function PlainNotice({
  message,
  retry,
  reload,
}: {
  message: string;
  retry: () => void;
  reload: () => void;
}) {
  const button = {
    borderRadius: 8,
    border: '1px solid var(--border)',
    background: 'var(--surface-2)',
    color: 'var(--text)',
    padding: '8px 14px',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
  } as const;
  return (
    <div
      role="alert"
      style={{
        minHeight: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'var(--canvas)',
        color: 'var(--text)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 640,
          borderRadius: 12,
          border: '1px solid var(--border)',
          background: 'var(--surface)',
          padding: 20,
          display: 'grid',
          gap: 14,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>This page hit an error</h1>
        <p style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)' }}>
          The app failed while it was being drawn. Nothing was written anywhere by this. The details
          are in the browser console.
        </p>
        <pre
          style={{
            margin: 0,
            overflowX: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            borderRadius: 8,
            border: '1px solid var(--border)',
            background: 'var(--surface-2)',
            padding: '8px 12px',
            fontSize: 12,
            color: 'var(--danger)',
          }}
        >
          {message}
        </pre>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={retry}
            style={{
              ...button,
              background: 'var(--primary)',
              color: '#fff',
              borderColor: 'transparent',
            }}
          >
            Try again
          </button>
          <button type="button" onClick={reload} style={button}>
            Reload
          </button>
        </div>
      </div>
    </div>
  );
}
